/**
 * User-facing API error messages. Centralizes copy so the UI stays actionable
 * instead of showing generic "permission" or raw Prisma/stack traces.
 */

const GENERIC_PERMISSION = 'You do not have permission to perform this action.'
const GENERIC_NOT_FOUND = 'We could not find what you requested. Refresh and try again.'
const GENERIC_CONFLICT = 'This item changed since you opened it. Refresh and try again.'
const GENERIC_SERVER = 'Something went wrong on our side. Please try again in a moment.'

/** Server messages that are too vague to show alone. */
const VAGUE_SERVER_PHRASES = new Set([
  'access denied',
  'insufficient permissions',
  'forbidden',
  'not found',
  'internal server error',
  'unauthorized',
])

function normalize(text) {
  return String(text || '').trim().toLowerCase()
}

/** True when the backend message is safe and useful to show verbatim. */
export function isUserFacingServerMessage(message) {
  const raw = String(message || '').trim()
  if (!raw) return false
  if (raw.length > 320) return false
  if (isTechnicalErrorMessage(raw)) return false
  if (VAGUE_SERVER_PHRASES.has(normalize(raw))) return false
  return true
}

/** True for Prisma, DB connectivity, and other non-user-facing failures. */
export function isTechnicalErrorMessage(message) {
  const raw = String(message || '')
  const n = normalize(raw)
  if (!raw) return false
  return (
    n.includes('prisma')
    || n.includes('invocation:')
    || n.includes("can't reach database")
    || n.includes('database server')
    || n.includes('econnrefused')
    || n.includes('connection refused')
    || n.includes('invalid `prisma')
    || n.includes('syntax error at')
    || n.includes('unexpected token')
    || (n.includes('error:') && raw.length > 180)
  )
}

function messageForTechnicalError({ status, rawMessage }) {
  const n = normalize(rawMessage)
  if (
    n.includes("can't reach database")
    || n.includes('database server')
    || n.includes('econnrefused')
    || n.includes('connection refused')
  ) {
    return 'We could not reach the database. If you are developing locally, start Postgres (for example: docker compose up -d postgres) and try again.'
  }
  if (status >= 500) return GENERIC_SERVER
  return null
}

function messageForStatusFallback(status) {
  if (status === 401) return 'Your session has expired. Please sign in again.'
  if (status === 403) return GENERIC_PERMISSION
  if (status === 404) return GENERIC_NOT_FOUND
  if (status === 409) return GENERIC_CONFLICT
  if (status === 429) return 'Too many requests. Wait a minute and try again.'
  if (status >= 500) return GENERIC_SERVER
  return null
}

function messageForEndpointAndStatus({ endpoint, status, rawMessage, code }) {
  const raw = String(rawMessage || '').trim()
  const n = normalize(raw)
  const path = String(endpoint || '')

  if (code === 'S3_NOT_CONFIGURED') {
    return 'File storage is not configured in this environment. Use local upload or configure S3 for your deployment.'
  }
  if (code === 'INFLUENCER_ROSTER_DUPLICATE') {
    return 'That creator is already on your roster. Open Influencers to update their profile.'
  }
  if (code === 'impersonation_session_expired') {
    return 'Your view-as session ended. Sign in again as a super admin, or use Exit view-as before signing out.'
  }

  if (path === '/campaigns' && status === 400 && n.includes('client is required')) {
    return 'Please choose a client before creating this campaign.'
  }

  if (path.includes('/invite-approved') && status === 400 && n.includes('upload slots')) {
    return raw || 'Create upload slots for each approved creator before sending invites.'
  }

  if (path.includes('/send-roster-to-client')) {
    if (n.includes('already pending client approval')) {
      return 'This roster is already waiting for client review. You can resend after they complete the current review.'
    }
    if (n.includes('no revised or new roster entries')) {
      return 'No new or revised roster entries are ready to send yet. Add or revise creators, then try again.'
    }
    if (n.includes('invalid proposed influencer transition')) {
      return 'We could not resend roster review because some entries are in a locked state. Refresh and try again.'
    }
  }

  if (path === '/auth/login') {
    if (status === 401) return raw || 'Invalid email or password. Please try again.'
    if (status === 403) {
      if (n.includes('workspace')) {
        return raw || 'This account belongs to a different workspace. Sign in from the correct URL for that agency.'
      }
      if (n.includes('password reset') || n.includes('setup link') || n.includes('invite')) {
        return raw
      }
      if (n.includes('archived')) return raw
      return raw || 'Sign-in was blocked for this account. Use your invite link to finish setup, or contact support.'
    }
  }

  if (path.includes('/auth/join') && status === 403 && n.includes('workspace')) {
    return raw || 'This invite link is for a different workspace. Open the link from your invite email.'
  }

  if (status === 403) {
    if (n.includes('view-as') || n.includes('super admin') || n.includes('super-admin')) {
      return raw
    }
    if (n.includes('only client')) return raw
    if (n.includes('only account manager') || n.includes('only influencers')) return raw
    if (n.includes('cannot view as')) return raw
    if (VAGUE_SERVER_PHRASES.has(n)) {
      return 'You do not have access to this item. If you think this is a mistake, ask your account manager.'
    }
  }

  if (status === 400) {
    if (n.includes('invalid') && n.includes('transition')) {
      return raw || 'This action is not allowed at the current step. Refresh the page and try again.'
    }
    if (n.includes('brief') && n.includes('10 characters')) return raw
    if (n.includes('submit') && n.includes('upload')) return raw
  }

  return null
}

/**
 * Map an API failure to copy suitable for alerts, snackbars, and ErrorState.
 */
export function getUserFriendlyApiErrorMessage({ endpoint, status, rawMessage, code } = {}) {
  const raw = String(rawMessage || '').trim()
  const statusNum = Number(status) || 0

  const technical = messageForTechnicalError({ status: statusNum, rawMessage: raw })
  if (technical) return technical

  const specific = messageForEndpointAndStatus({
    endpoint,
    status: statusNum,
    rawMessage: raw,
    code,
  })
  if (specific) return specific

  if (isUserFacingServerMessage(raw)) return raw

  const fallback = messageForStatusFallback(statusNum)
  if (fallback) return fallback

  return raw || `Request failed (${statusNum || 'unknown'}). Please review your input and try again.`
}

/**
 * Normalize any thrown value (usually api.js Error) for display.
 */
export function getApiErrorMessage(error) {
  if (!error) return GENERIC_SERVER
  if (typeof error === 'string') return error

  const message = String(error.message || '').trim()
  const raw = String(error.rawMessage || '').trim()
  const status = error.status
  const endpoint = error.endpoint
  const code = error.code

  if (message && !isTechnicalErrorMessage(message)) {
    const genericPermission = message === GENERIC_PERMISSION
    const genericServer = message === GENERIC_SERVER
    if ((genericPermission || genericServer) && isUserFacingServerMessage(raw)) {
      return raw
    }
    if (isUserFacingServerMessage(message)) return message
  }

  if (raw || status || endpoint) {
    return getUserFriendlyApiErrorMessage({ endpoint, status, rawMessage: raw || message, code })
  }

  return message || GENERIC_SERVER
}

/** Optional support detail (request id) for errors from apiRequest. */
export function getApiErrorDetail(error) {
  const requestId = error?.requestId
  if (!requestId) return null
  return `If you contact support, include reference ${requestId}.`
}

/** Primary message + optional detail for ErrorState / Alert. */
export function formatApiErrorForDisplay(error) {
  return {
    message: getApiErrorMessage(error),
    detail: getApiErrorDetail(error),
  }
}
